/**
 * [Phase 2b-B §4.4 / Phase 2c-D §6.1 + NEW-5] Step 4 - Draft Settings
 *
 * Auction mode (default): budget, bidding_timer_seconds, nomination_timer_seconds,
 *   antisnipe_trigger_seconds, antisnipe_extend_seconds. Anti-snipe extend
 *   must be >= trigger (auto-snap below).
 *
 * Snake mode (`isSnake=true`): pick_timer_enabled + pick_timer_seconds,
 *   allow_pick_resubmit. Hides budget + anti-snipe + bid/nom timers.
 *
 * [SNAKE TIMER REV] The pick timer is now a Yes/No question first. Yes →
 * 30 / 60 / 120 presets or Custom (minutes 0-10 + seconds 0-55 in 5s steps,
 * so 10:00 is the real cap and 0:10 the floor). No → untimed draft: the
 * engine starts no clock and never auto-picks. "Timer on" therefore implies
 * enforcement, which is why the old `auto_pick_on_timeout` checkbox is gone.
 *
 * NEW-5 (v1.2 §6.8): if a user sets `antisnipe_extend < antisnipe_trigger`,
 * we auto-snap extend up to == trigger (extend cannot meaningfully be less
 * than trigger - server-validates the same constraint). The snap fires once
 * on blur to avoid stomping mid-edit.
 */
import { useState } from "react";
import {
  PICK_TIMER_PRESETS,
  PICK_TIMER_MIN_SECONDS,
  PICK_TIMER_MAX_SECONDS,
  PICK_TIMER_CUSTOM_MINUTES,
  PICK_TIMER_CUSTOM_SECONDS,
  formatClock,
} from "../../utils/pickTimer";

const PRESET_BIDS = [15, 30, 45, 60];
const PRESET_NOMS = [60, 90, 120];
const PRESET_BUDGETS = [100, 150, 200, 250];

const Pill = ({ active, onClick, children, dataTestId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={dataTestId}
    className={`px-4 py-2 rounded-lg font-display font-bold text-sm uppercase transition-all ${
      active
        ? "bg-[#FF6B00] text-white"
        : "bg-[#252525] text-[#A0A0A0] hover:bg-[#333]"
    }`}
  >
    {children}
  </button>
);

// [TD-67 Section 2.2 / TD-66 micro-preview bundling] Pure helper computes the
// "{N} CPUs + {M} open seats + 1 commissioner = {T} teams" math. Extracted
// for deterministic edge-case testing without DOM rendering.
export function computeCpuPreviewParts(value, numTeams) {
  const cpu = Math.max(0, Number.isInteger(value) ? value : 0);
  const total = Math.max(1, Number.isInteger(numTeams) ? numTeams : 1);
  const open = Math.max(0, total - cpu - 1);
  return { cpu, open, total };
}

// [TD-66 Section 2.1] CPU Managers stepper - pre-lobby commissioner decision.
// Range 0..num_teams-1 (commissioner is always a human). Renders in both
// auction and snake branches of Step 4. Dark-only palette per H.1; wizard-
// wide day-mode parity tracked as TD-76.
function CpuManagersStepper({ form, update }) {
  const numTeams = form.num_teams || 12;
  const max = numTeams - 1;
  const value = Number.isInteger(form.cpu_count) ? form.cpu_count : 0;
  const atMin = value <= 0;
  const atMax = value >= max;

  const dec = () => {
    if (atMin) return;
    update({ cpu_count: value - 1 });
  };
  const inc = () => {
    if (atMax) return;
    update({ cpu_count: value + 1 });
  };

  return (
    <section className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5">
      <p className="font-display font-bold text-base uppercase tracking-wide text-[#F5F5F5]">
        CPU Managers
      </p>
      <p className="font-body text-sm text-[#A0A0A0] mb-4">
        Pre-seat the lobby with computer-controlled teams. They stagger in
        when you open the lobby. Range 0-{max}.
      </p>
      {/* [TD-67 Section 2.2] Micro-preview line - heads off "where does the
          Nth seat go?" confusion at decision time. Inherits Step 4's
          dark-only palette per H.1; no new tokens introduced. */}
      <p
        className="font-body text-xs text-[#888] mb-4"
        data-testid="wizard-cpu-count-preview"
      >
        {(() => {
          const { cpu, open, total } = computeCpuPreviewParts(value, numTeams);
          return `${cpu} CPUs + ${open} open seats + 1 commissioner = ${total} teams`;
        })()}
      </p>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={dec}
          disabled={atMin}
          data-testid="wizard-cpu-count-decrement"
          aria-label="Decrement CPU managers"
          className="w-12 h-12 rounded-full bg-[#252525] hover:bg-[#333] disabled:opacity-40 disabled:cursor-not-allowed text-[#F5F5F5] font-display font-black text-2xl flex items-center justify-center transition-colors"
        >
          −
        </button>
        <div
          data-testid="wizard-cpu-count-display"
          className="font-display font-black text-4xl text-[#FF6B00] w-16 text-center tabular-nums"
        >
          {value}
        </div>
        <button
          type="button"
          onClick={inc}
          disabled={atMax}
          data-testid="wizard-cpu-count-increment"
          aria-label="Increment CPU managers"
          className="w-12 h-12 rounded-full bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-black text-2xl flex items-center justify-center transition-colors"
        >
          +
        </button>
      </div>
    </section>
  );
}

// [SNAKE TIMER REV] Yes/No first, then presets or a Custom m:ss pair.
// `pick_timer_seconds` is only meaningful while the timer is enabled.
function PickTimerControl({ form, update }) {
  const enabled = form.pick_timer_enabled !== false;
  const seconds = Number.isInteger(form.pick_timer_seconds)
    ? form.pick_timer_seconds
    : 60;
  const [customOpen, setCustomOpen] = useState(
    () => !PICK_TIMER_PRESETS.includes(seconds),
  );
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  const tooShort = seconds < PICK_TIMER_MIN_SECONDS;

  const setCustom = (m, s) => {
    const next = Math.min(PICK_TIMER_MAX_SECONDS, m * 60 + s);
    update({ pick_timer_enabled: true, pick_timer_seconds: next });
  };

  return (
    <section
      className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5"
      data-testid="wizard-pick-timer-section"
    >
      <p className="font-display font-bold text-base uppercase tracking-wide text-[#F5F5F5]">
        Do you want a pick timer?
      </p>
      <p className="font-body text-sm text-[#A0A0A0] mb-4">
        With a timer on, a manager who runs out of time gets auto-picked for -
        same as an absent manager. With it off, picks stay open until they're
        made.
      </p>
      <div className="flex flex-wrap gap-2">
        <Pill
          active={enabled}
          onClick={() => update({ pick_timer_enabled: true })}
          dataTestId="wizard-pick-timer-yes"
        >
          Yes
        </Pill>
        <Pill
          active={!enabled}
          onClick={() => update({ pick_timer_enabled: false })}
          dataTestId="wizard-pick-timer-no"
        >
          No
        </Pill>
      </div>

      {enabled ? (
        <div className="mt-5" data-testid="wizard-pick-timer-length">
          <p className="font-display font-bold text-xs uppercase tracking-widest text-[#A0A0A0] mb-2">
            How long per pick?
          </p>
          <div className="flex flex-wrap gap-2">
            {PICK_TIMER_PRESETS.map((s) => (
              <Pill
                key={s}
                active={!customOpen && seconds === s}
                onClick={() => {
                  setCustomOpen(false);
                  update({ pick_timer_enabled: true, pick_timer_seconds: s });
                }}
                dataTestId={`wizard-pick-timer-${s}`}
              >
                {s}s
              </Pill>
            ))}
            <Pill
              active={customOpen}
              onClick={() => setCustomOpen(true)}
              dataTestId="wizard-pick-timer-custom"
            >
              Custom
            </Pill>
          </div>

          {customOpen && (
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1">
                <span className="font-display font-bold text-[10px] uppercase tracking-widest text-[#A0A0A0]">
                  Minutes
                </span>
                <select
                  value={minutes}
                  onChange={(e) => setCustom(parseInt(e.target.value, 10), remainder)}
                  data-testid="wizard-pick-timer-custom-minutes"
                  className="bg-[#252525] border border-[#333] rounded-lg px-3 py-2 font-display font-bold text-sm text-[#F5F5F5]"
                >
                  {PICK_TIMER_CUSTOM_MINUTES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-display font-bold text-[10px] uppercase tracking-widest text-[#A0A0A0]">
                  Seconds
                </span>
                <select
                  value={remainder}
                  onChange={(e) => setCustom(minutes, parseInt(e.target.value, 10))}
                  data-testid="wizard-pick-timer-custom-seconds"
                  className="bg-[#252525] border border-[#333] rounded-lg px-3 py-2 font-display font-bold text-sm text-[#F5F5F5]"
                >
                  {PICK_TIMER_CUSTOM_SECONDS.map((s) => (
                    <option key={s} value={s}>{String(s).padStart(2, "0")}</option>
                  ))}
                </select>
              </label>
              <p
                className="font-mono font-black text-2xl text-[#FF6B00] tabular-nums pb-1"
                data-testid="wizard-pick-timer-custom-preview"
              >
                {formatClock(seconds)}
              </p>
            </div>
          )}

          {tooShort && (
            <p
              className="font-body text-xs text-[#FF4444] mt-3"
              data-testid="wizard-pick-timer-min-warning"
            >
              Pick timer must be at least {formatClock(PICK_TIMER_MIN_SECONDS)}.
            </p>
          )}
        </div>
      ) : (
        <p
          className="font-body text-xs text-[#A0A0A0] mt-4"
          data-testid="wizard-pick-timer-untimed-note"
        >
          Untimed draft - no clock, no auto-picks. The commissioner can still
          draft for a manager who has gone missing.
        </p>
      )}
    </section>
  );
}

export default function Step4DraftSettings({ form, update, isSnake = false }) {
  if (isSnake) {
    return (
      <div data-testid="wizard-step-4-snake" className="space-y-8">
        <header>
          <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
            Snake Draft Settings
          </h2>
          <p className="font-body text-sm text-[#A0A0A0] mt-1">
            Pick timing and re-submission behavior for snake-format leagues.
          </p>
        </header>

        <PickTimerControl form={form} update={update} />

        {/* Allow pick resubmit */}
        <section className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.allow_pick_resubmit}
              onChange={(e) => update({ allow_pick_resubmit: e.target.checked })}
              data-testid="wizard-allow-pick-resubmit"
              className="mt-1 w-4 h-4 rounded accent-[#FF6B00]"
            />
            <div>
              <p className="font-display font-bold text-sm uppercase tracking-wide text-[#F5F5F5]">
                Allow pick re-submission
              </p>
              <p className="font-body text-xs text-[#A0A0A0] mt-1">
                Permits a manager to revise their pick before the timer expires. Recommended on.
              </p>
            </div>
          </label>
        </section>

        {/* [TD-66] CPU Managers stepper - snake branch */}
        <CpuManagersStepper form={form} update={update} />
      </div>
    );
  }
  return (
    <div data-testid="wizard-step-4" className="space-y-8">
      <header>
        <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
          Draft Settings
        </h2>
        <p className="font-body text-sm text-[#A0A0A0] mt-1">
          Set your auction budget and live-draft timers.
        </p>
      </header>

      {/* Budget */}
      <section className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5">
        <p className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0] mb-3">
          Auction Budget
        </p>
        <p className="font-display font-black text-5xl text-[#FF6B00]">
          ${form.budget}
        </p>
        <p className="font-body text-xs text-[#A0A0A0] mb-4">per manager</p>
        <input
          type="range"
          min="50"
          max="500"
          step="50"
          value={form.budget}
          onChange={(e) => update({ budget: parseInt(e.target.value, 10) })}
          data-testid="wizard-budget-slider"
          className="w-full"
        />
        <div className="flex gap-2 flex-wrap mt-3">
          {PRESET_BUDGETS.map((b) => (
            <Pill
              key={b}
              active={form.budget === b}
              onClick={() => update({ budget: b })}
              dataTestId={`wizard-budget-${b}`}
            >
              ${b}
            </Pill>
          ))}
        </div>
      </section>

      {/* Bidding timer */}
      <section className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5">
        <p className="font-display font-bold text-base uppercase tracking-wide text-[#F5F5F5]">
          Bid Timer
        </p>
        <p className="font-body text-sm text-[#A0A0A0] mb-4">
          How long bidders have to respond after each bid.
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESET_BIDS.map((s) => (
            <Pill
              key={s}
              active={form.bidding_timer_seconds === s}
              onClick={() => update({ bidding_timer_seconds: s })}
              dataTestId={`wizard-bid-timer-${s}`}
            >
              {s}s
            </Pill>
          ))}
        </div>
      </section>

      {/* Nomination timer */}
      <section className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5">
        <p className="font-display font-bold text-base uppercase tracking-wide text-[#F5F5F5]">
          Nomination Timer
        </p>
        <p className="font-body text-sm text-[#A0A0A0] mb-4">
          How long a manager has to nominate a player.
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESET_NOMS.map((s) => (
            <Pill
              key={s}
              active={form.nomination_timer_seconds === s}
              onClick={() => update({ nomination_timer_seconds: s })}
              dataTestId={`wizard-nom-timer-${s}`}
            >
              {s}s
            </Pill>
          ))}
        </div>
      </section>

      {/* Anti-snipe */}
      <section className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5">
        <p className="font-display font-bold text-base uppercase tracking-wide text-[#F5F5F5]">
          Anti-snipe
        </p>
        <p className="font-body text-sm text-[#A0A0A0] mb-4">
          When a bid lands inside the trigger window, the timer extends.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-xs font-medium text-[#A0A0A0] mb-1">
              Trigger threshold (seconds)
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={form.antisnipe_trigger_seconds}
              onChange={(e) =>
                update({
                  antisnipe_trigger_seconds: Math.max(
                    1,
                    parseInt(e.target.value || "1", 10),
                  ),
                })
              }
              data-testid="wizard-antisnipe-trigger"
              className="w-full bg-[#111111] border border-[#252525] focus:border-[#FF6B00] rounded-lg px-3 py-2 font-mono text-[#F5F5F5] outline-none"
            />
          </div>
          <div>
            <label className="block font-body text-xs font-medium text-[#A0A0A0] mb-1">
              Extend by (seconds)
            </label>
            <input
              type="number"
              min={1}
              max={60}
              value={form.antisnipe_extend_seconds}
              onChange={(e) =>
                update({
                  antisnipe_extend_seconds: Math.max(
                    1,
                    parseInt(e.target.value || "1", 10),
                  ),
                })
              }
              onBlur={() => {
                // [Phase 2c-D §6.7 NEW-5] Auto-snap extend UP to trigger when
                // user-entered extend < trigger. Fires on blur so we never
                // stomp mid-edit.
                if (form.antisnipe_extend_seconds < form.antisnipe_trigger_seconds) {
                  update({ antisnipe_extend_seconds: form.antisnipe_trigger_seconds });
                }
              }}
              data-testid="wizard-antisnipe-extend"
              className="w-full bg-[#111111] border border-[#252525] focus:border-[#FF6B00] rounded-lg px-3 py-2 font-mono text-[#F5F5F5] outline-none"
            />
          </div>
        </div>
        {form.antisnipe_extend_seconds < form.antisnipe_trigger_seconds && (
          <p className="font-body text-xs text-[#FF4444] mt-3">
            Extend must be ≥ trigger.
          </p>
        )}
      </section>

      {/* [TD-66] CPU Managers stepper - auction branch */}
      <CpuManagersStepper form={form} update={update} />
    </div>
  );
}

export const validateStep4 = (form) => {
  const isSnake = form.draftFormat === "snake";
  // [TD-66 B.1] cpu_count validation - runs for both branches.
  if (form.cpu_count !== undefined && form.cpu_count !== null) {
    if (!Number.isInteger(form.cpu_count)) return "CPU Managers must be a whole number.";
    if (form.cpu_count < 0) return "CPU Managers must be ≥ 0.";
    const maxCpu = (form.num_teams || 12) - 1;
    if (form.cpu_count > maxCpu) {
      return `CPU Managers must be ≤ ${maxCpu} (one seat reserved for the commissioner).`;
    }
  }
  if (isSnake) {
    if (form.pick_timer_enabled === false) return null;
    if (!Number.isInteger(form.pick_timer_seconds))
      return "Pick timer must be a whole number of seconds.";
    if (form.pick_timer_seconds < PICK_TIMER_MIN_SECONDS)
      return `Pick timer must be at least ${formatClock(PICK_TIMER_MIN_SECONDS)}.`;
    if (form.pick_timer_seconds > PICK_TIMER_MAX_SECONDS)
      return `Pick timer cannot exceed ${formatClock(PICK_TIMER_MAX_SECONDS)}.`;
    return null;
  }
  if (!Number.isInteger(form.bidding_timer_seconds) || form.bidding_timer_seconds < 1)
    return "Bid timer must be a positive integer.";
  if (!Number.isInteger(form.nomination_timer_seconds) || form.nomination_timer_seconds < 1)
    return "Nomination timer must be a positive integer.";
  if (form.budget < 50 || form.budget > 500)
    return "Budget must be between $50 and $500.";
  if (form.antisnipe_extend_seconds < form.antisnipe_trigger_seconds)
    return "Anti-snipe extend must be ≥ trigger.";
  return null;
};
